// -----------------------------------------------------------------------------
// Self-hosted HTTP storage adapter.
//
// This module keeps the original `firebase.ts` export surface (so no other
// app file needs to change) but routes collaboration scene/file persistence
// and shareable-link files to a self-hosted excalidraw-storage-backend over
// plain HTTP instead of Firebase/Firestore.
//
//   collab scene   -> PUT/GET {BACKEND}/rooms/:roomId   (iv ++ ciphertext)
//   collab + share -> PUT/GET {BACKEND}/files/:fileId
//
// `loadFirebaseStorage` is intentionally left as the real Firebase Storage
// initializer because it is only used by the Excalidraw+ cloud export, which
// is unrelated to self-hosting. It is harmless when unconfigured.
// -----------------------------------------------------------------------------
import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES, toBrandedType } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
  IV_LENGTH_BYTES,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";
import { initializeApp } from "firebase/app";
import { getStorage } from "firebase/storage";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { getSyncableElements } from ".";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";

// -----------------------------------------------------------------------------
// HTTP storage backend
// -----------------------------------------------------------------------------

// e.g. https://draw.ayelite-tech.com/api/v2  (no trailing slash)
const HTTP_STORAGE_BACKEND_URL = (
  import.meta.env.VITE_APP_HTTP_STORAGE_BACKEND_URL || ""
).replace(/\/$/, "");

const roomUrl = (roomId: string) => `${HTTP_STORAGE_BACKEND_URL}/rooms/${roomId}`;
const fileUrl = (fileId: string) => `${HTTP_STORAGE_BACKEND_URL}/files/${fileId}`;

// -----------------------------------------------------------------------------
// Firebase (kept only for Excalidraw+ cloud export, unused in self-host)
// -----------------------------------------------------------------------------

let FIREBASE_CONFIG: Record<string, any>;
try {
  FIREBASE_CONFIG = JSON.parse(import.meta.env.VITE_APP_FIREBASE_CONFIG);
} catch (error: any) {
  FIREBASE_CONFIG = {};
}

let firebaseApp: ReturnType<typeof initializeApp> | null = null;
let firebaseStorage: ReturnType<typeof getStorage> | null = null;

const _initializeFirebase = () => {
  if (!firebaseApp) {
    firebaseApp = initializeApp(FIREBASE_CONFIG);
  }
  return firebaseApp;
};

const _getStorage = () => {
  if (!firebaseStorage) {
    firebaseStorage = getStorage(_initializeFirebase());
  }
  return firebaseStorage;
};

export const loadFirebaseStorage = async () => {
  return _getStorage();
};

// -----------------------------------------------------------------------------
// encryption helpers
// -----------------------------------------------------------------------------

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array<ArrayBuffer> }> => {
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);
  const { encryptedBuffer, iv } = await encryptData(key, encoded);

  return { ciphertext: encryptedBuffer, iv };
};

const decryptElements = async (
  key: string,
  iv: Uint8Array<ArrayBuffer>,
  ciphertext: Uint8Array<ArrayBuffer> | ArrayBuffer,
): Promise<readonly ExcalidrawElement[]> => {
  const decrypted = await decryptData(iv, ciphertext, key);
  const decodedData = new TextDecoder("utf-8").decode(
    new Uint8Array(decrypted),
  );
  return JSON.parse(decodedData);
};

// Buffer layout stored in the backend is: [IV (IV_LENGTH_BYTES)] ++ [ciphertext]
const decryptElementsFromBuffer = async (
  buffer: ArrayBuffer,
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const iv = new Uint8Array(
    buffer.slice(0, IV_LENGTH_BYTES),
  ) as Uint8Array<ArrayBuffer>;
  const ciphertext = buffer.slice(IV_LENGTH_BYTES, buffer.byteLength);
  return decryptElements(roomKey, iv, ciphertext);
};

class HttpStorageSceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => {
    return HttpStorageSceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    HttpStorageSceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);

    return HttpStorageSceneVersionCache.get(portal.socket) === sceneVersion;
  }
  // if no room exists, consider the room saved so that we don't unnecessarily
  // prevent unload (there's nothing we could do at that point anyway)
  return true;
};

// -----------------------------------------------------------------------------
// files
// -----------------------------------------------------------------------------

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const response = await fetch(fileUrl(id), {
          method: "PUT",
          body: new Blob([buffer as BlobPart]),
        });
        if (response.ok) {
          savedFiles.push(id);
        } else {
          erroredFiles.push(id);
        }
      } catch (error: any) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await fetch(fileUrl(id));
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};

// -----------------------------------------------------------------------------
// collaboration scene
// -----------------------------------------------------------------------------

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (
    // bail if no room exists as there's nothing we can do at this point
    !roomId ||
    !roomKey ||
    !socket ||
    isSavedToFirebase(portal, elements)
  ) {
    return null;
  }

  // read the currently stored scene so we can reconcile with it. There is no
  // transaction (unlike Firestore) so this is best-effort; the backend write
  // is last-writer-wins, which is acceptable for a self-hosted instance.
  let prevStoredElements: SyncableExcalidrawElement[] = [];
  const getResponse = await fetch(roomUrl(roomId));
  if (getResponse.ok) {
    const buffer = await getResponse.arrayBuffer();
    if (buffer.byteLength > IV_LENGTH_BYTES) {
      prevStoredElements = getSyncableElements(
        restoreElements(
          await decryptElementsFromBuffer(buffer, roomKey),
          null,
        ),
      );
    }
  } else if (getResponse.status !== 404) {
    return null;
  }

  const reconciledElements = getSyncableElements(
    reconcileElements(
      elements,
      prevStoredElements as OrderedExcalidrawElement[] as RemoteExcalidrawElement[],
      appState,
    ),
  );

  const { ciphertext, iv } = await encryptElements(roomKey, reconciledElements);

  // Concatenate IV with encrypted data (IV does not have to be secret).
  const putResponse = await fetch(roomUrl(roomId), {
    method: "PUT",
    body: new Blob([iv, ciphertext]),
  });

  if (!putResponse.ok) {
    return null;
  }

  const storedElements = getSyncableElements(
    restoreElements(reconciledElements, null),
  );

  HttpStorageSceneVersionCache.set(socket, storedElements);

  return toBrandedType<RemoteExcalidrawElement[]>(storedElements);
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const getResponse = await fetch(roomUrl(roomId));
  if (!getResponse.ok) {
    return null;
  }
  const buffer = await getResponse.arrayBuffer();
  if (buffer.byteLength <= IV_LENGTH_BYTES) {
    return null;
  }

  const elements = getSyncableElements(
    restoreElements(await decryptElementsFromBuffer(buffer, roomKey), null, {
      deleteInvisibleElements: true,
    }),
  );

  if (socket) {
    HttpStorageSceneVersionCache.set(socket, elements);
  }

  return elements;
};
